<template>
  <v-data-table
    :loading="loading"
    :headers="headers"
    :items="getFilteredTransactions"
    sort-by="date"
    :sort-desc="true"
    :items-per-page="15"
    :mobile-breakpoint="0"
    :group-by="groupBy ? 'groupDate' : []"
    :group-desc="true"
    dense
    hide-default-footer
    :page.sync="page"
    @page-count="pageCount = $event"
  >
    <template v-slot:group.header="{ group }">
      <td>{{ group }}</td>
      <td></td>
      <td>
        {{ getGroupTotal(group) }}
        <span class="caption ml-1">{{ getUnitById(group) }}</span>
      </td>
      <td>
        {{ getGroupTotalConvert(group) }}
        <span class="caption ml-1">{{ getProdUnitById(group) }}</span>
      </td>
      <td>
        {{ getGroupTotalCorrection(group) }}
        <span class="caption ml-1">{{ getUnitById(group) }}</span>
      </td>
    </template>
    <template v-slot:item.date="{ item }">
      <span class="caption">{{ item.date | denseDateHourFormat }}</span>
    </template>

    <template v-slot:item.product="{ item }">
      <span class="caption">{{ getProductName(item) }}</span>
    </template>
    <template v-slot:item.from_recipe="{ item }">
      <span class="caption mr-1" v-if="item.from_recipe_quantity !== null">{{
        item.from_recipe_quantity | round2
      }}</span>
      <span class="caption">{{ getRecipeName(item) }}</span>
    </template>
    <template v-slot:item.quantity="{ item }">
      <span class="caption">{{ Number(item.quantity) | round2 }}</span>
      <span class="ml-1 caption before-correction"
        >({{ Number(item.quantity_before_correction) | round2 }})</span
      >
      <span class="overline ml-1">{{ getUnit(item) }}</span>
    </template>
    <template v-slot:item.quantity_convert="{ item }">
      <span class="caption" v-if="getIfConversion(item)">{{
        getProdQty(item) | round2
      }}</span>
      <span class="ml-1 caption before-correction" v-if="getIfConversion(item)"
        >({{ getProdQtyBeforeCorr(item) | round2 }})</span
      >
      <span v-if="getIfConversion(item)" class="overline ml-1">{{
        getProdUnit(item)
      }}</span>
    </template>
    <template v-slot:item.correction="{ item }">
      <span
        v-if="item.correction && Number(item.correction) !== 0"
        class="caption"
        >{{ Number(item.correction) | round2 }}</span
      >
      <span
        v-if="item.correction && Number(item.correction) !== 0"
        class="overline ml-1"
        >{{ getUnit(item) }}</span
      >
    </template>
    <template v-slot:footer>
      <v-pagination
        color="accent"
        v-model="page"
        :length="pageCount"
        :total-visible="5"
      ></v-pagination>
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from "vuex";
import { api } from "@/api";
import { mappers } from "@/mappers";
import { round10 } from "round10";
import moment from "moment";
export default {
  name: "TransactionsGrid",
  props: {
    take: {
      required: true
    },
    productFilter: {
      required: true
    },
    start: {
      required: true
    },
    end: {
      required: true
    },
    groupBy: {
      required: true
    }
  },
  created() {
    this.getData();
  },
  computed: {
    ...mapGetters([
      "getProducts",
      "getRecipes",
      "getUncompletedIntegratedTransactions"
    ]),
    getFilteredTransactions() {
      return this.productFilter
        ? this.data.filter(x => x.fk_product === this.productFilter)
        : this.data;
    },
    getUncompletedIntegratedTransactionsLength() {
      return this.getUncompletedIntegratedTransactions.length;
    }
  },
  data() {
    return {
      data: [],
      loading: false,
      page: 1,
      pageCount: 0,
      headers: [
        { value: "date", text: "", sortable: false, class: "px-1" },
        {
          value: "from_recipe",
          text: "Provenance",
          sortable: false,
          class: "px-1"
        },
        {
          value: "quantity",
          text: "Qté. Inv.",
          sortable: false,
          class: "px-1"
        },
        {
          value: "quantity_convert",
          text: "Qté. Prod.",
          sortable: false,
          class: "px-1"
        },
        {
          value: "correction",
          text: "Ajustement",
          sortable: false,
          class: "px-1"
        }
      ]
    };
  },
  methods: {
    async getData() {
      if (this.take !== null || this.start !== null) {
        try {
          this.loading = true;

          const data = await api.getInventoryTransactions({
            take: this.take,
            product: this.productFilter,
            start:
              this.start != null
                ? moment(this.start)
                    .hours(0)
                    .minutes(0)
                    .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
                : this.start,
            end:
              this.end != null
                ? moment(this.end)
                    .hours(23)
                    .minutes(59)
                    .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
                : this.end
          });
          this.data = data.map(x => {
            return { ...x, groupDate: moment(x.date).format("YYYY-MM-DD") };
          });
        } finally {
          this.loading = false;
        }
      }
    },
    getProductName() {
      return this.getProducts.find(x => x.id === this.productFilter)
        ? this.getProducts.find(x => x.id === this.productFilter).nom
        : "";
    },
    getRecipeName(item) {
      const r = this.getRecipes.find(x => x.id === item.from_recipe);
      if (r) {
        return r.nom;
      }
      return "";
    },
    getProductNameById(id) {
      return this.getProducts.find(x => x.id == id)
        ? this.getProducts.find(x => x.id == id).nom
        : "";
    },
    getUnit() {
      return this.getProducts.find(x => x.id === this.productFilter)
        ? this.getProducts.find(x => x.id === this.productFilter).data
          ? this.getProducts.find(x => x.id === this.productFilter).data
              .unite_mesure_inventory
          : ""
        : "";
    },
    getUnitById(id) {
      return this.getProducts.find(x => x.id == id)
        ? this.getProducts.find(x => x.id == id).data
          ? this.getProducts.find(x => x.id == id).data.unite_mesure_inventory
          : ""
        : "";
    },
    getProdUnitById(id) {
      return this.getProducts.find(x => x.id == id)
        ? this.getProducts.find(x => x.id == id).data &&
          this.getProducts.find(x => x.id == id).data.use_conversion_formula
          ? this.getProducts.find(x => x.id == id).data.unite_mesure
          : ""
        : "";
    },
    getProdQty(item) {
      const product = this.getProducts.find(x => x.id === this.productFilter);
      if (product && product.data.use_conversion_formula) {
        return round10(
          mappers.productInvToProd(item.quantity, product.data),
          -4
        );
      } else {
        return null;
      }
    },
    getProdQtyBeforeCorr(item) {
      const product = this.getProducts.find(x => x.id === this.productFilter);
      if (product && product.data.use_conversion_formula) {
        return round10(
          mappers.productInvToProd(
            item.quantity_before_correction,
            product.data
          ),
          -4
        );
      } else {
        return null;
      }
    },
    getProdUnit() {
      const product = this.getProducts.find(x => x.id === this.productFilter);
      if (product && product.data.use_conversion_formula) {
        return product.data.unite_mesure;
      } else {
        return null;
      }
    },
    getIfConversion() {
      const product = this.getProducts.find(x => x.id === this.productFilter);
      return product && product.data.use_conversion_formula;
    },
    getGroupTotal(date) {
      return round10(
        this.data
          .filter(x => x.groupDate == date)
          .map(x => Number(x.quantity))
          .reduce((a, b) => a + b, 0),
        -4
      );
    },
    getGroupTotalCorrection(date) {
      return round10(
        this.data
          .filter(x => x.groupDate == date)
          .map(x => Number(x.correction))
          .reduce((a, b) => a + b, 0),
        -4
      );
    },
    getGroupTotalConvert(date) {
      const product = this.getProducts.find(x => x.id === this.productFilter);
      if (product && product.data.use_conversion_formula) {
        return round10(
          mappers.productInvToProd(
            this.data
              .filter(x => x.groupDate == date)
              .map(x => Number(x.quantity))
              .reduce((a, b) => a + b, 0),
            product.data
          ),
          -4
        );
      } else {
        return null;
      }
    }
  },
  watch: {
    take(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    productFilter(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    getUncompletedIntegratedTransactionsLength(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    start(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    },
    end(v, oldv) {
      if (v !== oldv) {
        this.getData();
      }
    }
  }
};
</script>

<style scoped>
.before-correction {
  opacity: 0.55;
}
</style>
